import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Menu,
  Paper,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useNavigate } from 'react-router-dom'
import { formatHumanName, normalizeErrorString } from '@medplum/core'
import type { Communication, Practitioner } from '@medplum/fhirtypes'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { useSubscription } from '@medplum/react-hooks'
import {
  IconArchive,
  IconArchiveOff,
  IconCircleOff,
  IconDots,
  IconMessageCircle,
  IconPaperclip,
  IconUser,
} from '@tabler/icons-react'
import { useMiddlewareClient } from '@/hooks'
import { formatDateTime } from '@/utils'
import { logError } from '@/utils/errorUtils'
import {
  ACCESS_POLICY_BADGE_LABELS,
  checkThreadStatus,
  getCommunicationFromSubscriptionBundle,
  getThreadLabels,
  isArchivedThread,
  THREAD_LABEL_FOLLOW_UP,
  THREAD_LABEL_URGENT,
  type MessageThreadSummary,
  type PractitionerThreadOption,
  type ThreadMessage,
  updateThreadArchivedState,
} from '../service'
import { formatParticipantList, getOtherParticipants } from '../presentation'
import { CARD_CLASS } from '../tokens'
import { useThreadMessageSender } from '../useThreadMessageSender'
import { MessageList } from './MessageList'
import { ParticipantAvatarGroup } from './ParticipantAvatarGroup'
import { ThreadMessageComposer } from './ThreadMessageComposer'

type ThreadDetailsProps = {
  threadSummary?: MessageThreadSummary
  onThreadRead?: (threadId: string) => void
  onThreadUpdated?: (thread: MessageThreadSummary) => void
  minimized?: boolean
}

function getPractitionerLabel(practitioner: Practitioner | undefined): string {
  if (!practitioner) {
    return 'Unknown practitioner'
  }

  return formatHumanName(practitioner.name?.[0]) || practitioner.id || 'Unknown practitioner'
}

function getParticipantLabel(participant: PractitionerThreadOption | undefined): string {
  return getPractitionerLabel(participant?.practitioner)
}

export const ThreadDetails = ({
  threadSummary,
  onThreadRead,
  onThreadUpdated,
  minimized = false,
}: ThreadDetailsProps) => {
  const medplum = useMedplum()
  const navigate = useNavigate()
  const middleware = useMiddlewareClient()
  const profile = useMedplumProfile()
  const practitionerProfile = profile?.resourceType === 'Practitioner' ? profile : undefined

  const [messages, setMessages] = useState<ThreadMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [updatingArchiveState, setUpdatingArchiveState] = useState(false)
  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const shouldScrollToBottomRef = useRef(false)
  const threadSummaryRef = useRef(threadSummary)
  const messagesRef = useRef<ThreadMessage[]>([])
  const composerFocusedRef = useRef(false)
  const markingReadRef = useRef(false)

  const appendMessage = useCallback((message: ThreadMessage) => {
    setMessages((prev) =>
      prev.some((item) => item.message.id === message.message.id) ? prev : [...prev, message],
    )
    shouldScrollToBottomRef.current = true
  }, [])

  const messageSender = useThreadMessageSender({ threadSummary, onCreated: appendMessage })

  const handleSendText = useCallback(
    (text: string) => {
      shouldScrollToBottomRef.current = true
      return messageSender.send(text)
    },
    [messageSender],
  )

  useEffect(() => {
    threadSummaryRef.current = threadSummary
  }, [threadSummary])

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  /** The single place this component marks a thread read. */
  const applyThreadRead = useCallback(
    async (threadId: string): Promise<void> => {
      const readResult = (await middleware.post('/threads/read', { threadId })) as {
        thread: MessageThreadSummary['thread']
      }
      threadSummaryRef.current = threadSummaryRef.current
        ? { ...threadSummaryRef.current, thread: readResult.thread }
        : threadSummaryRef.current
      onThreadRead?.(threadId)
    },
    [middleware, onThreadRead],
  )

  /**
   * Mark the open thread read when the user is demonstrably looking at it.
   *
   * Opening a thread marks it read once, but a conversation left open goes
   * stale: later messages arrive through the subscription and were never
   * acknowledged, so the sender kept seeing Sent even while the recipient was
   * reading. Composer focus is treated as presence, so anything that arrives
   * while the cursor sits in the box is read immediately.
   *
   * `latestOverride` exists because the subscription calls this before its
   * `setMessages` has committed — the newly arrived message is passed directly
   * rather than read from stale state.
   */
  const markThreadReadIfNeeded = useCallback(
    async (latestOverride?: Communication): Promise<void> => {
      const summary = threadSummaryRef.current
      const threadId = summary?.thread.id
      const practitionerId = practitionerProfile?.id

      if (!summary || !threadId || !practitionerId || markingReadRef.current) {
        return
      }

      // A thread left focused on a hidden tab must not report Read for
      // messages the user never actually saw.
      if (document.visibilityState !== 'visible') {
        return
      }

      const latestMessage =
        latestOverride ?? messagesRef.current[messagesRef.current.length - 1]?.message
      if (!latestMessage) {
        return
      }

      if (checkThreadStatus(summary.thread, practitionerId, latestMessage) !== 'unread') {
        return
      }

      markingReadRef.current = true
      try {
        await applyThreadRead(threadId)
      } catch (err) {
        logError('messages', 'Failed to mark thread read from composer presence', err)
      } finally {
        markingReadRef.current = false
      }
    },
    [applyThreadRead, practitionerProfile?.id],
  )

  const handleComposerPresenceChange = useCallback(
    (present: boolean) => {
      composerFocusedRef.current = present
      if (present) {
        void markThreadReadIfNeeded()
      }
    },
    [markThreadReadIfNeeded],
  )

  // Returning to the tab with the composer still focused counts as reading too.
  useEffect(() => {
    const handleVisibilityChange = (): void => {
      if (composerFocusedRef.current) {
        void markThreadReadIfNeeded()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [markThreadReadIfNeeded])

  useEffect(() => {
    if (!threadSummary?.thread.id) {
      setMessages([])
      setError(null)
      return
    }

    let cancelled = false
    const threadId = threadSummary.thread.id

    async function loadThreadMessages(): Promise<void> {
      setLoading(true)
      setError(null)
      try {
        const result = (await middleware.get('/threads/messages', {
          threadId,
          count: 500,
        })) as ThreadMessage[]
        if (!cancelled) {
          setMessages(result)
          shouldScrollToBottomRef.current = true

          const latestMessage = result[result.length - 1]?.message
          const currentThread = threadSummaryRef.current?.thread
          // Deliberately not gated on tab visibility: explicitly opening a
          // thread is an unambiguous read, unlike passive composer presence.
          if (
            practitionerProfile?.id &&
            currentThread &&
            latestMessage &&
            checkThreadStatus(currentThread, practitionerProfile.id, latestMessage) === 'unread'
          ) {
            await applyThreadRead(threadId)
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(normalizeErrorString(err))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadThreadMessages().catch((err) => logError('messages', 'Failed to load thread messages', err))

    return () => {
      cancelled = true
    }
  }, [applyThreadRead, middleware, practitionerProfile?.id, threadSummary?.thread.id])

  useLayoutEffect(() => {
    const container = messagesContainerRef.current
    if (!container) {
      return
    }

    if (!shouldScrollToBottomRef.current) {
      return
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    })
    shouldScrollToBottomRef.current = false
    // Optimistic bubbles append to the end too, so a new pending send has to
    // scroll the history just like a confirmed message does.
  }, [messages, messageSender.pending.length])

  useSubscription(
    threadSummary?.thread.id
      ? `Communication?part-of=Communication/${threadSummary.thread.id}`
      : undefined,
    (bundle) => {
      if (!threadSummary) {
        return
      }

      const communication = getCommunicationFromSubscriptionBundle(bundle)
      if (!communication?.id) {
        return
      }

      const incomingSenderId = communication.sender?.reference?.replace('Practitioner/', '')

      // Retire the optimistic bubble as soon as its real message lands, which
      // may be before `createThreadMessage` has even resolved.
      messageSender.reconcile(communication, incomingSenderId)

      // Sticky presence: while the cursor is in the composer, an incoming
      // message is read the moment it arrives — no further interaction needed.
      if (incomingSenderId !== practitionerProfile?.id && composerFocusedRef.current) {
        void markThreadReadIfNeeded(communication)
      }

      setMessages((prev) => {
        const existingIndex = prev.findIndex((item) => item.message.id === communication.id)
        const knownParticipants = [threadSummary.sender, ...threadSummary.recipients].filter(
          (participant): participant is PractitionerThreadOption =>
            Boolean(participant?.practitioner?.id),
        )
        const sender = knownParticipants.find(
          (participant) =>
            `Practitioner/${participant.practitioner.id}` === communication.sender?.reference,
        )
        const recipients = (communication.recipient ?? [])
          .map((recipient) =>
            knownParticipants.find(
              (participant) =>
                `Practitioner/${participant.practitioner.id}` === recipient.reference,
            ),
          )
          .map((participant) => participant?.practitioner)
          .filter((participant): participant is Practitioner => Boolean(participant))
        const nextItem: ThreadMessage = {
          message: communication,
          sender: sender?.practitioner,
          recipients,
        }

        if (existingIndex >= 0) {
          return prev.map((item, index) =>
            index === existingIndex ? { ...item, ...nextItem } : item,
          )
        }

        shouldScrollToBottomRef.current = true
        return [...prev, nextItem]
      })
    },
  )

  if (!threadSummary) {
    return (
      <Paper
        withBorder
        radius="md"
        className={
          minimized
            ? 'flex h-full min-h-0 items-center justify-center p-5'
            : 'flex h-full min-h-0 items-center justify-center p-8'
        }
      >
        <div className="text-center text-[#7D7987]">
          <Text fw={600} c="dark" size={minimized ? 'sm' : 'md'}>
            Select a thread
          </Text>
          <Text size={minimized ? 'xs' : 'sm'}>
            Choose a conversation from the list to view its messages.
          </Text>
        </div>
      </Paper>
    )
  }

  const otherParticipants = getOtherParticipants(threadSummary, practitionerProfile?.id)
  const archived = isArchivedThread(threadSummary.thread)

  const handleArchiveToggle = async (): Promise<void> => {
    setUpdatingArchiveState(true)
    try {
      const updatedThread = await updateThreadArchivedState(
        medplum,
        threadSummary.thread,
        !archived,
      )
      onThreadUpdated?.({
        ...threadSummary,
        thread: updatedThread,
      })
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff size={16} />,
        title: archived ? 'Unable to unarchive thread' : 'Unable to archive thread',
        message: normalizeErrorString(err),
      })
    } finally {
      setUpdatingArchiveState(false)
    }
  }

  const messagesEmptyState = (
    <div className="flex h-full items-center justify-center text-center text-[#7D7987]">
      <div>
        <Text fw={600} c="dark">
          No messages in this thread
        </Text>
        <Text size="sm">The thread exists, but there are no child messages yet.</Text>
      </div>
    </div>
  )

  if (!minimized) {
    // The role chip describes one person, so it is dropped on a group thread
    // rather than attributing one member's access policy to everybody.
    const soleParticipant = otherParticipants.length === 1 ? otherParticipants[0] : undefined
    const threadLabels = getThreadLabels(threadSummary.thread)
    const isUrgent = threadLabels.includes(THREAD_LABEL_URGENT)
    const isFollowUp = threadLabels.includes(THREAD_LABEL_FOLLOW_UP)
    const relatedPatientId = threadSummary.relatedPatient?.id

    return (
      <div className={['flex h-full min-h-0 flex-col', CARD_CLASS].join(' ')}>
        <div className="flex shrink-0 items-center gap-3 border-b border-[#ECEEF1] px-5 py-3.5">
          <ParticipantAvatarGroup participants={otherParticipants} size={40} expanded />

          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-[17px] font-extrabold tracking-[-0.3px] text-[#1F2733]">
              {threadSummary.thread.topic?.text || 'Untitled thread'}
            </h2>
            <p className="truncate text-sm font-medium text-[#6B7280]">
              {formatParticipantList(otherParticipants)}
              {soleParticipant
                ? ` · ${ACCESS_POLICY_BADGE_LABELS[soleParticipant.accessPolicyName]}`
                : ''}
              {threadSummary.relatedPatient
                ? ` · Patient: ${formatHumanName(threadSummary.relatedPatient.name?.[0])}`
                : ''}
            </p>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {isUrgent && (
              <span className="behuman-v2-mulish rounded bg-[#FEE2E2] px-2 py-1 text-[11px] font-bold tracking-wide text-[#EF4444] uppercase">
                Urgent
              </span>
            )}
            {isFollowUp && (
              <span className="behuman-v2-mulish rounded bg-[#FEF3C7] px-2 py-1 text-[11px] font-bold tracking-wide text-[#B45309] uppercase">
                Follow up
              </span>
            )}
            {archived && (
              <span className="behuman-v2-mulish rounded bg-[#F1F2F4] px-2 py-1 text-[11px] font-bold tracking-wide text-[#6B7280] uppercase">
                Archived
              </span>
            )}

            <Button
              variant="default"
              size="xs"
              radius="md"
              className="behuman-v2-mulish !font-bold !text-[#1F2733]"
              styles={{ root: { borderColor: '#E4E7EB' } }}
              loading={updatingArchiveState}
              leftSection={archived ? <IconArchiveOff size={16} /> : <IconArchive size={16} />}
              onClick={() => void handleArchiveToggle()}
            >
              {archived ? 'Unarchive' : 'Archive'}
            </Button>

            <Menu position="bottom-end" radius="md" shadow="md" width={220}>
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  radius="md"
                  size="lg"
                  aria-label="More actions"
                >
                  <IconDots size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                {relatedPatientId && (
                  <Menu.Item
                    leftSection={<IconUser size={16} />}
                    onClick={() => navigate(`/patients/${relatedPatientId}`)}
                  >
                    Open patient chart
                  </Menu.Item>
                )}
                <Menu.Item
                  leftSection={archived ? <IconArchiveOff size={16} /> : <IconArchive size={16} />}
                  onClick={() => void handleArchiveToggle()}
                >
                  {archived ? 'Unarchive thread' : 'Archive thread'}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </div>
        </div>

        <div
          ref={messagesContainerRef}
          // Focusable so the history can be scrolled by keyboard. A text-only
          // thread contains nothing focusable, so without this a keyboard user
          // cannot reach the scroll area at all (WCAG 2.1.1).
          tabIndex={0}
          role="region"
          aria-label="Message history"
          className="focus-visible:outline-primary min-h-0 flex-1 overflow-y-auto bg-[#F6F7F9] px-5 py-4 outline-none focus-visible:outline-2 focus-visible:-outline-offset-2"
        >
          {loading && (
            <div className="flex h-full items-center justify-center">
              <Loader />
            </div>
          )}
          {!loading && error && <Alert color="red">{error}</Alert>}
          {!loading &&
            !error &&
            messages.length === 0 &&
            messageSender.pending.length === 0 &&
            messagesEmptyState}
          {!loading && !error && (messages.length > 0 || messageSender.pending.length > 0) && (
            <MessageList
              messages={messages}
              currentPractitionerId={practitionerProfile?.id}
              currentPractitioner={practitionerProfile}
              thread={threadSummary.thread}
              otherParticipants={otherParticipants}
              pending={messageSender.pending}
              onRetry={messageSender.retry}
            />
          )}
        </div>

        {!archived && (
          <div className="shrink-0 border-t border-[#ECEEF1]">
            <ThreadMessageComposer
              threadSummary={threadSummary}
              compact
              onSendText={handleSendText}
              onCreated={appendMessage}
              onPresenceChange={handleComposerPresenceChange}
              onActivity={() => void markThreadReadIfNeeded()}
            />
          </div>
        )}
      </div>
    )
  }

  return (
    <Paper withBorder radius="md" className="flex h-full flex-col">
      <div className="flex h-full flex-col">
        <div
          className={[
            'flex shrink-0 items-start justify-between gap-4 border-b border-slate-200',
            minimized ? 'px-3 py-3' : 'px-5 py-4',
          ].join(' ')}
        >
          <div className="min-w-0">
            <Text fw={700} size={minimized ? 'md' : 'lg'}>
              {threadSummary.thread.topic?.text || 'Untitled thread'}
            </Text>
            <Group gap="xs" mt={minimized ? 4 : 0}>
              <Text size={minimized ? 'xs' : 'sm'} c="dimmed">
                with{' '}
                {otherParticipants.length > 0
                  ? otherParticipants.map(getParticipantLabel).join(', ')
                  : 'no other participants'}
              </Text>
              {otherParticipants.length === 1 && (
                <Badge variant="light" color="blue" radius="sm" size={minimized ? 'xs' : 'sm'}>
                  {ACCESS_POLICY_BADGE_LABELS[otherParticipants[0].accessPolicyName]}
                </Badge>
              )}
              {archived && (
                <Badge variant="light" color="gray" radius="sm" size={minimized ? 'xs' : 'sm'}>
                  Archived
                </Badge>
              )}
            </Group>
            <Group gap="xs" mt={2}>
              {threadSummary.relatedPatient && (
                <Text size={minimized ? 'xs' : 'sm'} c="dark">
                  Related Patient: {formatHumanName(threadSummary.relatedPatient.name?.[0])}
                </Text>
              )}
            </Group>
          </div>

          <Tooltip label={archived ? 'Unarchive thread' : 'Archive thread'}>
            <ActionIcon
              variant="light"
              color={archived ? 'blue' : 'gray'}
              size={minimized ? 'md' : 'lg'}
              loading={updatingArchiveState}
              onClick={() => void handleArchiveToggle()}
              aria-label={archived ? 'Unarchive thread' : 'Archive thread'}
            >
              {archived ? (
                <IconArchiveOff size={minimized ? 16 : 18} />
              ) : (
                <IconArchive size={minimized ? 16 : 18} />
              )}
            </ActionIcon>
          </Tooltip>
        </div>

        <div
          ref={messagesContainerRef}
          // Same keyboard-scroll fix as the redesigned panel above.
          tabIndex={0}
          role="region"
          aria-label="Message history"
          className={[
            'focus-visible:outline-primary min-h-0 flex-1 overflow-y-auto outline-none focus-visible:outline-2 focus-visible:-outline-offset-2',
            minimized ? 'p-3' : 'p-5',
          ].join(' ')}
        >
          {loading && (
            <div className="flex h-full items-center justify-center">
              <Loader />
            </div>
          )}

          {!loading && error && <Alert color="red">{error}</Alert>}

          {!loading && !error && messages.length === 0 && messagesEmptyState}

          {!loading && !error && messages.length > 0 && (
            <Stack gap={minimized ? 'sm' : 'md'}>
              {messages.map(({ message, sender }) => {
                const isMine = sender?.id === practitionerProfile?.id

                return (
                  <div
                    key={message.id}
                    className={['flex', isMine ? 'justify-end' : 'justify-start'].join(' ')}
                  >
                    <div
                      className={[
                        minimized
                          ? 'max-w-[88%] rounded-2xl px-3 py-2.5'
                          : 'max-w-[75%] rounded-2xl px-4 py-3',
                        isMine ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-900',
                      ].join(' ')}
                    >
                      <div className="mb-1 flex items-center gap-2">
                        <IconMessageCircle size={minimized ? 12 : 14} />
                        <Text size={minimized ? 'xs' : 'sm'} fw={600} c={isMine ? 'white' : 'dark'}>
                          {getPractitionerLabel(sender)}
                        </Text>
                      </div>
                      <Text
                        size={minimized ? 'xs' : 'sm'}
                        c={isMine ? 'white' : 'dark'}
                        className="[overflow-wrap:anywhere] break-words whitespace-pre-wrap"
                      >
                        {message.payload?.[0]?.contentString || ''}
                      </Text>
                      {message.payload?.[0]?.contentAttachment?.url && (
                        <a
                          href={message.payload[0].contentAttachment.url}
                          target="_blank"
                          rel="noreferrer"
                          className={[
                            minimized
                              ? 'mt-2 inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs underline'
                              : 'mt-2 inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm underline',
                            isMine ? 'bg-white/10 text-white' : 'bg-white text-slate-900',
                          ].join(' ')}
                        >
                          <IconPaperclip size={minimized ? 12 : 14} />
                          {message.payload[0].contentAttachment.title || 'Open attachment'}
                        </a>
                      )}
                      {!message.payload?.[0]?.contentString &&
                        !message.payload?.[0]?.contentAttachment?.url && (
                          <Text size={minimized ? 'xs' : 'sm'} c={isMine ? 'white' : 'dark'}>
                            Empty message
                          </Text>
                        )}
                      <Text size="xs" mt={8} c={isMine ? 'rgba(255,255,255,0.8)' : 'dimmed'}>
                        {message.sent ? formatDateTime(message.sent, '-', true) : ''}
                      </Text>
                    </div>
                  </div>
                )
              })}
            </Stack>
          )}
        </div>

        {!archived && (
          <div className="shrink-0">
            <ThreadMessageComposer
              threadSummary={threadSummary}
              minimized={minimized}
              onCreated={appendMessage}
              onPresenceChange={handleComposerPresenceChange}
              onActivity={() => void markThreadReadIfNeeded()}
            />
          </div>
        )}
      </div>
    </Paper>
  )
}
